/**
 * Landing page (DESIGN 6.6 step 1-2): name (remembered), robe colour,
 * Create room / Join with a code, auto-join from `#CODE`, error toasts.
 *
 * It also owns the connection handshake, because create/join may only go
 * out after the server's `welcome`: a click stores the frame as `pending`,
 * (re)connects when the hello would differ from the one on the wire, and
 * sends the frame from the `welcome` handler. On first load a reconnect
 * token from a refresh within the server's window is offered so a mid-wave
 * refresh lands back in the game (DESIGN 5.7).
 *
 * One quirk is handled on purpose: `Socket` keeps the token it learned, and
 * a hello carrying a token for a player who already left is answered with
 * `welcome` followed by `err no_room ("session expired")`. That error is
 * expected whenever we re-hello (changed name, or an expired resume) and is
 * swallowed once; the next `no_room` (a bad join code) is shown.
 */

import type { ClientMsg, ErrCode, ServerMsg } from '@shared/index.js';
import { MAX_NAME_LENGTH, PLAYER_COLORS, ROOM_CODE_LENGTH, colorName } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import { type Socket, defaultWsUrl } from '../net/socket.js';
import { type Screen, button, clear, el, hide, keycap, setText, show, storageGet, storageSet, toast } from './dom.js';
import {
  STORAGE_KEYS, describeError, giveUpText, nameOrRandom, normalizeCode, parseRoomHash, roomLink, sanitizeColor, sanitizeName,
} from './helpers.js';

/** How long a token resume may stay silent before the landing shows anyway. */
const RESUME_GRACE_MS = 4000;
/** Repeated identical error codes within this window are shown once. */
const ERROR_REPEAT_MS = 2000;

interface HelloSent { name: string; color: number }

export function mount(root: HTMLElement, state: ClientState, socket: Socket): Screen {
  // ---------------------------------------------------------------- DOM
  const nameInput = el('input', {
    class: 'input input-name', type: 'text', maxlength: MAX_NAME_LENGTH, autocomplete: 'off', spellcheck: 'false',
    placeholder: 'Your wizard name (blank = random)', 'aria-label': 'Wizard name',
  });
  const randomBtn = button('Random', () => { nameInput.value = nameOrRandom(''); nameInput.focus(); }, 'btn btn-ghost');
  const colorRow = el('div', { class: 'colors', role: 'radiogroup', 'aria-label': 'Robe colour' });
  const createBtn = button('Create room', () => submit({ t: 'create' }), 'btn btn-primary btn-wide');
  const codeInput = el('input', {
    class: 'input input-code', type: 'text', maxlength: ROOM_CODE_LENGTH, autocomplete: 'off', spellcheck: 'false',
    placeholder: 'CODE', 'aria-label': 'Room code',
  });
  const joinBtn = button('Join', () => submitJoin(), 'btn btn-wide');
  const status = el('div', { class: 'landing-status', 'aria-live': 'polite' });
  const connBanner = el('div', { class: 'conn-banner', role: 'status' });
  hide(connBanner);

  const panel = el('div', { class: 'landing-panel' },
    el('h1', { class: 'title' }, 'Spellweave'),
    el('p', { class: 'tagline' }, 'Queue elements, cross the streams, apologise to your friends.'),
    el('label', { class: 'field' }, el('span', { class: 'label' }, 'Name'), el('div', { class: 'row' }, nameInput, randomBtn)),
    el('div', { class: 'field' }, el('span', { class: 'label' }, 'Robe'), colorRow),
    el('div', { class: 'actions' },
      createBtn,
      el('div', { class: 'or' }, 'or'),
      el('div', { class: 'row join-row' }, codeInput, joinBtn),
    ),
    status,
    el('div', { class: 'landing-help' },
      el('div', null, keycap('Q W E R'), ' ', keycap('A S D F'), ' queue elements'),
      el('div', null, keycap('LMB'), ' force ', keycap('Shift + LMB'), ' area ', keycap('MMB'), ' self ', keycap('V'), ' weapon ', keycap('Space'), ' magick'),
      el('div', null, keycap('RMB'), ' / arrows walk, ', keycap('Tab'), ' spellbook in a match, ', keycap('Enter'), ' chat, ', keycap('M'), ' mute'),
      el('div', null, keycap('Esc'), ' menu (pauses when you play alone), ', keycap('N'), ' ready: skip the wait'),
    ),
  );
  clear(root);
  root.classList.add('landing');
  root.append(panel, connBanner);

  // ---------------------------------------------------------------- state
  let color = sanitizeColor(storageGet(STORAGE_KEYS.color));
  const swatches: HTMLButtonElement[] = [];
  let pending: ClientMsg | null = null;
  let helloSent: HelloSent | null = null;
  let swallowNoRoom = false;
  let resuming = false;
  let suppressed = false;
  let resumeTimer: number | null = null;
  let lastErr: { code: string; at: number } | null = null;
  const unsubs: (() => void)[] = [];

  nameInput.value = sanitizeName(storageGet(STORAGE_KEYS.name) ?? '');
  state.localName = nameInput.value;
  state.localColor = color;
  renderColors();

  function renderColors(): void {
    if (swatches.length === 0) {
      PLAYER_COLORS.forEach((hex, i) => {
        const b = button('', () => { color = i; storageSet(STORAGE_KEYS.color, String(i)); state.localColor = i; renderColors(); }, 'swatch');
        b.style.setProperty('--swatch', hex);
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-label', colorName(i));
        b.title = colorName(i);
        swatches.push(b);
        colorRow.appendChild(b);
      });
    }
    // Marked in place, not rebuilt, so a keyboard player keeps focus on the swatch they just chose.
    swatches.forEach((b, i) => {
      b.classList.toggle('swatch-on', i === color);
      b.setAttribute('aria-checked', String(i === color));
    });
  }

  function setBusy(busy: boolean, text = ''): void {
    createBtn.disabled = busy;
    joinBtn.disabled = busy;
    setText(status, text);
  }

  function updateVisibility(): void {
    const visible = !suppressed && !state.inRoom && !resuming;
    if (root.hidden === !visible) return;
    root.hidden = !visible;
    if (visible && document.activeElement !== codeInput) nameInput.focus();
  }

  // ---------------------------------------------------------------- handshake

  /** Connect (or reuse the open connection when the hello would be identical) and send `msg` after `welcome`. */
  function submit(msg: ClientMsg, resumeToken: string | null = null): void {
    const name = nameOrRandom(nameInput.value);
    nameInput.value = name;
    storageSet(STORAGE_KEYS.name, name);
    state.localName = name;
    state.localColor = color;
    pending = msg;
    setBusy(true, 'Connecting...');
    if (socket.connected && helloSent && helloSent.name === name && helloSent.color === color) {
      socket.send(msg);
      return;
    }
    // A learned or offered token rides along on the hello; if its player is gone the server says so once.
    swallowNoRoom = socket.token !== null || resumeToken !== null;
    helloSent = { name, color };
    socket.connect(defaultWsUrl(), resumeToken ? { name, color, token: resumeToken } : { name, color });
  }

  function submitJoin(): void {
    const code = normalizeCode(codeInput.value);
    codeInput.value = code;
    if (code.length !== ROOM_CODE_LENGTH) {
      toast(`A room code is ${ROOM_CODE_LENGTH} letters`, 'error');
      codeInput.focus();
      return;
    }
    submit({ t: 'join', code });
  }

  /** A refresh within the reconnect window: hello with the stored token and wait for the room. */
  function tryResume(): void {
    const token = state.token;
    if (!token) return;
    resuming = true;
    updateVisibility();
    const name = sanitizeName(nameInput.value);
    helloSent = { name, color };
    swallowNoRoom = true;
    socket.connect(defaultWsUrl(), { name, color, token });
    resumeTimer = window.setTimeout(() => endResume(), RESUME_GRACE_MS);
  }

  function endResume(): void {
    if (resumeTimer !== null) {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    }
    if (!resuming) return;
    resuming = false;
    updateVisibility();
    if (!state.inRoom) autoJoinFromHash();
  }

  function autoJoinFromHash(): void {
    const code = parseRoomHash(window.location.hash);
    if (!code || state.inRoom || pending) return;
    codeInput.value = code;
    // A remembered name means a returning player following a friend's link: skip the click.
    if (sanitizeName(nameInput.value).length > 0) submit({ t: 'join', code });
    else codeInput.focus();
  }

  function onMsg(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome':
        if (pending) socket.send(pending);
        break;
      case 'room':
        swallowNoRoom = false;
        pending = null;
        setBusy(false);
        if (window.location.hash !== `#${msg.code}`) window.history.replaceState(null, '', roomLink(msg.code, window.location));
        endResume();
        updateVisibility();
        break;
      case 'err':
        onErr(msg.code, msg.msg);
        break;
      default:
        break;
    }
  }

  function onErr(code: ErrCode, msg: string): void {
    // No create/join in flight: the error is about the session we were already in.
    const joining = pending !== null;
    if (code === 'no_room' && swallowNoRoom) {
      swallowNoRoom = false;
      if (resuming) endResume();
      return;
    }
    swallowNoRoom = false;
    if (code === 'no_room' || code === 'room_full' || code === 'room_limit' || code === 'bad_version' || code === 'name_taken' || code === 'room_crashed') {
      pending = null;
      setBusy(false);
      if (resuming) endResume();
      if (code === 'no_room' || code === 'room_crashed') {
        window.history.replaceState(null, '', window.location.pathname);
      }
      updateVisibility();
    }
    const now = performance.now();
    if (lastErr && lastErr.code === code && now - lastErr.at < ERROR_REPEAT_MS) return;
    lastErr = { code, at: now };
    toast(describeError(code, msg, joining), 'error');
  }

  // ---------------------------------------------------------------- wiring
  unsubs.push(socket.onAny(onMsg));
  // The server closed us for good (another tab took the session, abuse limits): no reconnect is
  // coming, so leave the frozen room view for the landing page and say why.
  unsubs.push(socket.onGiveUp((code) => {
    pending = null;
    setBusy(false);
    if (resuming) endResume();
    if (state.inRoom) state.leaveRoom();
    updateVisibility();
    const text = giveUpText(code);
    if (text) toast(text, 'error', 8000);
  }));
  unsubs.push(socket.onStatus((connected, attempt) => {
    if (connected || attempt === 0) {
      hide(connBanner);
      if (connected && pending) setText(status, 'Joining...');
    } else {
      setText(connBanner, `Connection lost. Reconnecting (attempt ${attempt})...`);
      show(connBanner);
      if (pending) setText(status, `Reconnecting (attempt ${attempt})...`);
    }
  }));
  unsubs.push(state.subscribe(() => updateVisibility()));

  nameInput.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    if (normalizeCode(codeInput.value).length === ROOM_CODE_LENGTH) submitJoin();
    else submit({ t: 'create' });
  });
  nameInput.addEventListener('change', () => { nameInput.value = sanitizeName(nameInput.value); });
  codeInput.addEventListener('input', () => {
    const pos = codeInput.selectionStart;
    codeInput.value = normalizeCode(codeInput.value);
    if (pos !== null) codeInput.setSelectionRange(Math.min(pos, codeInput.value.length), Math.min(pos, codeInput.value.length));
  });
  codeInput.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    submitJoin();
  });
  const onHash = (): void => { if (!state.inRoom && !resuming) autoJoinFromHash(); };
  window.addEventListener('hashchange', onHash);
  unsubs.push(() => window.removeEventListener('hashchange', onHash));

  updateVisibility();
  if (state.token) tryResume();
  else autoJoinFromHash();

  return {
    show(): void {
      suppressed = false;
      updateVisibility();
    },
    hide(): void {
      suppressed = true;
      updateVisibility();
    },
    dispose(): void {
      for (const off of unsubs) off();
      if (resumeTimer !== null) window.clearTimeout(resumeTimer);
      clear(root);
    },
  };
}
